import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import MiniDatePicker from "@/components/MiniDatePicker";

// 단계 위저드(2026-07-18 피드백 — "A to Z, step별로 어떻게 하는지 알려주기") — 상단
// 단계 표시기가 현재 위치를 보여주고, 단계가 바뀔 때마다 본문이 페이드로 전환된다.
// 처음 쓰는 관리자도 표시기만 따라가면 끝나는 흐름.
const STEPS = ["대상 지정", "수량·사유", "완료"];

function Stepper({ current }) {
  return (
    <div className="mb-5 flex items-center">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = current > n;
        const active = current === n;
        return (
          <div key={label} className="flex flex-1 items-center last:flex-none">
            <div className="flex items-center gap-1.5">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold transition-colors duration-300 ${
                  done ? "bg-brand-600 text-white" : active ? "bg-brand-600 text-white shadow-[0_0_0_4px_rgba(59,130,246,0.15)]" : "bg-ink-100 text-ink-400"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : n}
              </span>
              <span className={`whitespace-nowrap text-xs font-medium ${active || done ? "text-ink-900" : "text-ink-400"}`}>
                {label}
              </span>
            </div>
            {n < STEPS.length && (
              <div className={`mx-2 h-px flex-1 transition-colors duration-300 ${done ? "bg-brand-400" : "bg-ink-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// 특정 가입일(범위)에 가입한 사용자 전체, 또는 붙여넣은 이메일 목록(수십~수백 명)에게
// 한 번에 크레딧을 지급/회수 — 단건 CreditAdjustForm과 같은 원장 기록을 유저마다 반복
// 적용한다(백엔드 _admin_bulk_adjust). 대상 확정(미리보기) → 금액/사유 입력 → 재확인
// → 적용의 3단계로, 실수로 큰 인원에게 잘못 지급하는 걸 막는다(2026-07-15).
const LARGE_MAGNITUDE_THRESHOLD = 100_000;

// 자동 재생 튜토리얼(2026-07-18 피드백 — "토스처럼, 실제 사용자가 행동하는 것처럼") —
// 세 장면(이메일 붙여넣기 → 수량·사유 입력 → 완료)이 2.6초 간격으로 순환하며, 장면 안
// 요소들이 사람이 입력하듯 시차를 두고 나타난다. 처음 여는 관리자가 글을 읽기 전에
// 눈으로 흐름을 익히게 하는 용도라 1단계에서만 보여준다.
const DEMO_SCENES = ["① 이메일을 붙여넣고", "② 수량·사유를 적고", "③ 확인하면 끝"];

function GrantDemo() {
  const [scene, setScene] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setScene((s) => (s + 1) % DEMO_SCENES.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="mb-4 rounded-xl bg-ink-50/80 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-ink-700">이렇게 진행돼요</span>
        <span key={scene} className="animate-fade-in rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700">
          {DEMO_SCENES[scene]}
        </span>
      </div>
      <div key={`s${scene}`} className="h-[4.5rem]">
        {scene === 0 && (
          <div className="space-y-1">
            <DemoLine delay={0}>a@sedaily.com</DemoLine>
            <DemoLine delay={0.5}>b@gmail.com</DemoLine>
            <DemoLine delay={1.0}>c@naver.com</DemoLine>
          </div>
        )}
        {scene === 1 && (
          <div className="space-y-1.5">
            <DemoField delay={0} label="1인당 수량" value="50,000 크레딧" />
            <DemoField delay={0.6} label="사유" value="7월 가입 이벤트 보상" />
          </div>
        )}
        {scene === 2 && (
          <div className="flex h-full items-center gap-2.5">
            <span className="demo-pop flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_4px_12px_rgba(16,185,129,0.35)]">
              <Check className="h-5 w-5" />
            </span>
            <div className="animate-fade-in" style={{ animationDelay: "0.25s", animationFillMode: "both" }}>
              <div className="text-sm font-semibold text-ink-900">24명 지급 완료</div>
              <div className="text-[11px] text-ink-400">모든 내역이 사유와 함께 기록됩니다</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DemoLine({ delay, children }) {
  return (
    <div
      className="animate-fade-in rounded-md bg-white px-2 py-1 font-mono text-[11px] text-ink-600 shadow-sm"
      style={{ animationDelay: `${delay}s`, animationFillMode: "both" }}
    >
      {children}
    </div>
  );
}

function DemoField({ delay, label, value }) {
  return (
    <div
      className="animate-fade-in flex items-center gap-2 rounded-md bg-white px-2.5 py-1.5 text-[11px] shadow-sm"
      style={{ animationDelay: `${delay}s`, animationFillMode: "both" }}
    >
      <span className="shrink-0 text-ink-400">{label}</span>
      <span className="font-medium tabular-nums text-ink-800">{value}</span>
    </div>
  );
}

export default function BulkCreditGrant({ onSuccess }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [mode, setMode] = useState("dateRange"); // "dateRange" | "emails"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [emailsText, setEmailsText] = useState("");

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { matchedCount, matched, unmatchedEmails }
  const [previewError, setPreviewError] = useState(null);

  const [direction, setDirection] = useState("give"); // "give" | "take"
  const [magnitude, setMagnitude] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { succeededCount, failedCount, failed }
  const [submitError, setSubmitError] = useState(null);

  const parsedEmails = emailsText
    .split(/[\n,]/)
    .map((e) => e.trim())
    .filter(Boolean);

  const targets = mode === "dateRange"
    ? { mode: "dateRange", from: dateFrom, to: dateTo }
    : { mode: "emails", emails: parsedEmails };

  const targetsReady = mode === "dateRange"
    ? Boolean(dateFrom && dateTo && dateFrom <= dateTo)
    : parsedEmails.length > 0;

  const mag = Number(magnitude);
  const validMagnitude = Number.isInteger(mag) && mag > 0;
  const signedAmount = direction === "give" ? mag : -mag;
  const matchedCount = preview?.matchedCount || 0;

  function resetAfterTargetChange() {
    setPreview(null);
    setPreviewError(null);
    setResult(null);
    setSubmitError(null);
    setConfirming(false);
  }

  async function handlePreview() {
    if (!targetsReady) return;
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const res = await analyticsApi.adminBulkPreview(targets);
      setPreview(res);
    } catch (err) {
      setPreviewError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  }

  function validateApply() {
    if (!validMagnitude) return "1 이상의 정수 크레딧을 입력하세요.";
    if (reason.trim().length < 3) return "사유를 3자 이상 입력하세요.";
    if (!matchedCount) return "대상자가 없습니다.";
    return null;
  }

  function handleApplyClick(e) {
    e.preventDefault();
    const err = validateApply();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);
    if (!confirming) {
      setConfirming(true); // 대상이 여러 명이라 항상 한 번 더 확인
      return;
    }
    applyBulk();
  }

  async function applyBulk() {
    if (readOnly) { setSubmitError("STG 조회 상태에서는 일괄 지급/회수가 비활성화됩니다."); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await analyticsApi.adminBulkAdjust({ targets, amount: signedAmount, reason: reason.trim() });
      setResult(res);
      setConfirming(false);
      // 목록 표의 잔액을 즉시(낙관적) 반영 — 성공한 대상(매칭됨 - 실패)에게만 증감액을 적용한다.
      // preview.matched 는 최대 200명까지라, 그보다 많으면 초과분은 백그라운드 리페치로 반영된다.
      const failedIds = new Set((res.failed || []).map((f) => f.userId));
      const affectedUserIds = (preview?.matched || [])
        .map((u) => u.userId)
        .filter((id) => !failedIds.has(id));
      onSuccess?.({ userIds: affectedUserIds, amount: signedAmount });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : String(err));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  }

  function handleStartOver() {
    setPreview(null);
    setPreviewError(null);
    setResult(null);
    setSubmitError(null);
    setConfirming(false);
    setMagnitude("");
    setReason("");
  }

  // ---- 3단계: 결과 ----
  if (result) {
    return (
      <div className="animate-fade-in space-y-3">
        <Stepper current={3} />
        <div className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {result.requested}명 중 <b>{result.succeededCount}명</b> 처리 완료
          {result.failedCount > 0 && <>, <b>{result.failedCount}명</b> 실패</>}
        </div>
        {result.failedCount > 0 && (
          <div className="rounded-xl bg-red-50 p-3">
            <div className="mb-1.5 text-xs font-medium text-red-700">실패한 사용자</div>
            <div className="max-h-48 space-y-1 overflow-y-auto text-xs">
              {result.failed.map((f) => (
                <div key={f.userId} className="flex items-center justify-between text-red-700">
                  <span className="truncate">{f.email || f.userId}</span>
                  <span className="shrink-0 pl-2 text-red-500">{f.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={handleStartOver}
          className="rounded-lg bg-ink-100 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-200"
        >
          새로 지급하기
        </button>
      </div>
    );
  }

  return (
    <div key={preview ? "step2" : "step1"} className="animate-fade-in space-y-4">
      <Stepper current={preview ? 2 : 1} />
      {!preview && <GrantDemo />}
      {/* 1단계: 대상 지정 */}
      <div>
        <div className="mb-1.5 text-sm font-medium text-ink-700">
          {preview ? "대상 지정" : "누구에게 지급할지 정합니다"}
        </div>
        <div className="mb-2 flex gap-2">
          <ModeBtn active={mode === "dateRange"} onClick={() => { setMode("dateRange"); resetAfterTargetChange(); }}>
            가입일 범위
          </ModeBtn>
          <ModeBtn active={mode === "emails"} onClick={() => { setMode("emails"); resetAfterTargetChange(); }}>
            이메일 목록
          </ModeBtn>
        </div>

        {mode === "dateRange" ? (
          <div className="flex flex-wrap items-center gap-2">
            <MiniDatePicker
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(v) => { setDateFrom(v); resetAfterTargetChange(); }}
            />
            <span className="shrink-0 text-xs text-ink-400">→</span>
            <MiniDatePicker
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(v) => { setDateTo(v); resetAfterTargetChange(); }}
            />
            <span className="w-full text-xs text-ink-400">이 기간에 가입한 사용자 전원이 대상이 됩니다.</span>
          </div>
        ) : (
          <div>
            <textarea
              value={emailsText}
              onChange={(e) => { setEmailsText(e.target.value); resetAfterTargetChange(); }}
              placeholder={"이메일을 한 줄에 하나씩(또는 쉼표로 구분) 붙여넣으세요\n예: a@example.com\nb@example.com"}
              rows={5}
              className="w-full rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-300"
            />
            <div className="mt-1 text-xs text-ink-400">{parsedEmails.length}개 입력됨</div>
          </div>
        )}
      </div>

      {!preview && (
        <button
          type="button"
          onClick={handlePreview}
          disabled={!targetsReady || previewing}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-500 disabled:opacity-40"
        >
          {previewing ? "확인 중…" : "대상 확인 → 다음 단계"}
        </button>
      )}

      {previewError && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{previewError}</div>
      )}

      {/* 2단계: 미리보기 + 금액/사유 */}
      {preview && (
        <>
          <div className="rounded-xl bg-ink-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-ink-900">{matchedCount}명 매칭됨</span>
              <button type="button" onClick={handleStartOver} className="text-xs text-ink-400 underline hover:text-ink-600">
                대상 다시 지정
              </button>
            </div>
            {matchedCount > 0 && (
              <div className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs text-ink-500">
                {preview.matched.map((u) => (
                  <div key={u.userId} className="flex items-center justify-between">
                    <span className="truncate">{u.email || u.userId}</span>
                    <span className="shrink-0 pl-2 text-ink-400">{u.createdAt}</span>
                  </div>
                ))}
                {matchedCount > preview.matched.length && (
                  <div className="text-ink-400">외 {matchedCount - preview.matched.length}명…</div>
                )}
              </div>
            )}
            {preview.unmatchedEmails?.length > 0 && (
              <div className="mt-2 border-t border-ink-200 pt-2">
                <div className="text-xs font-medium text-amber-700">매칭 안 됨 ({preview.unmatchedEmails.length})</div>
                <div className="mt-1 max-h-20 overflow-y-auto text-xs text-amber-600">
                  {preview.unmatchedEmails.join(", ")}
                </div>
              </div>
            )}
          </div>

          {matchedCount === 0 ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              조건에 맞는 사용자가 없습니다. 대상을 다시 지정해 주세요.
            </div>
          ) : (
            <form onSubmit={handleApplyClick} className="space-y-3">
              <div>
                <div className="mb-1 text-sm text-ink-600">지급 / 회수</div>
                <div className="flex gap-2">
                  <ToggleBtn active={direction === "give"} onClick={() => { setDirection("give"); setConfirming(false); }} tone="good">
                    지급 (+)
                  </ToggleBtn>
                  <ToggleBtn active={direction === "take"} onClick={() => { setDirection("take"); setConfirming(false); }} tone="bad">
                    회수 (-)
                  </ToggleBtn>
                </div>
              </div>
              <label className="block text-sm">
                <div className="mb-1 text-ink-600">1인당 수량 (크레딧)</div>
                <input
                  type="number"
                  min="1"
                  value={magnitude}
                  onChange={(e) => { setMagnitude(e.target.value); setConfirming(false); }}
                  placeholder="예: 50000"
                  className="w-full rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-300"
                />
              </label>
              <label className="block text-sm">
                <div className="mb-1 text-ink-600">사유 (필수)</div>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); setConfirming(false); }}
                  placeholder="예: 7/15 가입 이벤트 보상"
                  className="w-full rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-300"
                />
              </label>

              {validMagnitude && (
                <div className="rounded-lg bg-ink-50 px-3 py-2 text-sm">
                  <span className="text-ink-500">총 {matchedCount}명 ×</span>{" "}
                  <span className="font-semibold tabular-nums text-ink-900">{fmt.nc(signedAmount)}</span>{" "}
                  <span className="text-ink-500">= 총</span>{" "}
                  <span className="font-semibold tabular-nums text-ink-900">{fmt.nc(signedAmount * matchedCount)} 크레딧</span>
                </div>
              )}

              {submitError && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
              )}

              {confirming && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {matchedCount}명에게 {direction === "give" ? "지급" : "회수"}이 적용됩니다. 정말 진행하시겠습니까?
                  {mag >= LARGE_MAGNITUDE_THRESHOLD && " (1인당 큰 금액입니다)"}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || readOnly}
                className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50 ${
                  confirming ? "bg-amber-500 hover:bg-amber-600" : "bg-brand-600 hover:bg-brand-500"
                }`}
              >
                {submitting ? "처리 중…" : readOnly ? "STG 조회 전용" : confirming ? "확인하고 일괄 적용" : "일괄 적용"}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        active ? "bg-brand-600 text-white" : "bg-ink-50 text-ink-500 hover:bg-ink-100"
      }`}
    >
      {children}
    </button>
  );
}

function ToggleBtn({ active, onClick, tone, children }) {
  const activeClass = tone === "good"
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500"
    : "bg-red-50 text-red-700 ring-1 ring-red-500";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        active ? activeClass : "bg-ink-50 text-ink-500 hover:bg-ink-100"
      }`}
    >
      {children}
    </button>
  );
}
